import type { ForecastCostpointBreakdown } from '@finapp/types';
import type React from 'react';
import { getCostpointLabel } from '../utils/costpointLabels';
import { formatEuro } from '../utils/currency';

interface CostpointCoverageCalloutProps {
  costpointBreakdown: ForecastCostpointBreakdown[];
  orgProjectedNet: number;
}

/**
 * Splits the per-costpoint breakdown into those projecting a surplus (projected_net > 0)
 * and those projecting a deficit (projected_net < 0). Costpoints projecting exactly €0 are
 * left out of both groups - there's nothing to "cover" there.
 */
function partitionCoverage(breakdown: ForecastCostpointBreakdown[]): {
  surpluses: ForecastCostpointBreakdown[];
  deficits: ForecastCostpointBreakdown[];
} {
  return {
    surpluses: breakdown.filter((b) => b.projected_net > 0),
    deficits: breakdown.filter((b) => b.projected_net < 0),
  };
}

/** Joins a list of strings with an Oxford-comma-style "and" before the last item. */
function formatList(items: string[]): string {
  if (items.length === 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  const last = items[items.length - 1];
  return `${items.slice(0, -1).join(', ')}, and ${last}`;
}

/** Describes one group (all-surplus or all-deficit) of costpoints in a single clause. */
function describeGroup(group: ForecastCostpointBreakdown[], kind: 'surplus' | 'deficit'): string {
  const names = formatList(group.map((b) => getCostpointLabel(b.costpoint)));

  if (group.length === 1) {
    return `${names} is projecting a ${kind} of ${formatEuro(group[0]?.projected_net ?? 0)}`;
  }

  const total = group.reduce((sum, b) => sum + b.projected_net, 0);
  const plural = kind === 'surplus' ? 'surpluses' : 'shortfalls';
  return `${names} are projecting combined ${plural} of ${formatEuro(total)}`;
}

/**
 * Org-summary callout surfacing an otherwise-implicit fact: one costpoint's projected
 * surplus effectively offsetting another's projected deficit, since there's only one real
 * bank account behind all costpoints. Purely informational - derived client-side from
 * `costpoint_breakdown`, nothing is moved or transferred. Reuses the server-computed
 * `org_projected_net` for the bottom-line figure rather than re-summing the breakdown, so
 * it can never drift from the backend's own number (e.g. it already folds in the opening
 * balance, which a naive surplus-minus-deficit sum would miss).
 *
 * Renders nothing when there's no actual surplus-covering-deficit relationship to show:
 * all costpoints on the same side (all surplus, all deficit), or none projecting a
 * nonzero net at all.
 */
const CostpointCoverageCallout: React.FC<CostpointCoverageCalloutProps> = ({
  costpointBreakdown,
  orgProjectedNet,
}) => {
  const { surpluses, deficits } = partitionCoverage(costpointBreakdown);

  if (surpluses.length === 0 || deficits.length === 0) {
    return null;
  }

  const isCovered = orgProjectedNet >= 0;

  const coverageSentence = isCovered
    ? `Org-wide, that surplus covers the shortfall, leaving a projected net of ${formatEuro(
        orgProjectedNet
      )}.`
    : `Org-wide, that surplus doesn't fully cover the shortfall - the year is still projected to net ${formatEuro(
        orgProjectedNet
      )}.`;

  return (
    <div
      data-testid="costpoint-coverage-callout"
      className={`mb-4 border-l-4 p-4 rounded-md ${
        isCovered ? 'bg-blue-50 border-blue-400' : 'bg-red-50 border-red-400'
      }`}
    >
      <h4
        className={`text-xs font-medium uppercase tracking-wide mb-1 ${
          isCovered ? 'text-blue-800' : 'text-red-800'
        }`}
      >
        Cross-costpoint coverage
      </h4>
      <p className={`text-sm ${isCovered ? 'text-blue-700' : 'text-red-700'}`}>
        {describeGroup(surpluses, 'surplus')}, while {describeGroup(deficits, 'deficit')}.{' '}
        {coverageSentence}
      </p>
    </div>
  );
};

export default CostpointCoverageCallout;
